import { Empty, theme as antdTheme } from 'antd'
import { TicketCard } from 'features/libra/dashboard/ticketDashboard/TicketCard'
import type {
  CardView,
  PillCategories,
} from 'features/libra/dashboard/ticketDashboard/types'
import VirtualList from 'rc-virtual-list'
import type { FC } from 'react'
import { memo, useCallback } from 'react'
import { useMeasure } from 'react-use'
import styled from 'styled-components'

const ESTIMATED_CARD_HEIGHT = 120

/** rc-virtual-list's default thumb is translucent black, which disappears on the dark theme. */
const FillArea = styled.div<{ $thumbColor: string }>`
  flex: 1;
  min-height: 0;
  --rc-virtual-list-scrollbar-bg: ${({ $thumbColor }) => $thumbColor};
`

/** Right padding keeps cards clear of the overlaid scrollbar. */
const CardSlot = styled.div`
  padding: 0 0.75rem 0.5rem 0;
`

interface VirtualTicketListProps {
  cards: CardView[]
  categories: PillCategories
  onSelect: (cardId: string) => void
}

/**
 * Fills its flex parent. rc-virtual-list needs a pixel height, so the space is
 * measured and the list is drawn once it's known.
 */
const RawVirtualTicketList: FC<VirtualTicketListProps> = ({
  cards,
  categories,
  onSelect,
}) => {
  const { token } = antdTheme.useToken()
  const [measureRef, { height }] = useMeasure<HTMLDivElement>()
  const renderCard = useCallback(
    (card: CardView) => (
      <CardSlot>
        <TicketCard card={card} categories={categories} onSelect={onSelect} />
      </CardSlot>
    ),
    [categories, onSelect]
  )

  return (
    <FillArea ref={measureRef} $thumbColor={token.colorTextTertiary}>
      {cards.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description='No tickets' />
      ) : (
        height > 0 && (
          <VirtualList
            data={cards}
            height={height}
            itemHeight={ESTIMATED_CARD_HEIGHT}
            itemKey='id'
            showScrollBar>
            {renderCard}
          </VirtualList>
        )
      )}
    </FillArea>
  )
}

export const VirtualTicketList = memo(RawVirtualTicketList)
